import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getRequest, postRequest, putRequest, deleteRequest, ResponseDataType } from 'api';
import { Table, Button, Modal, Form, Input, Card, Space, Tag, Spin } from 'antd';
import { DatePicker, parseFlexibleDate } from 'Components';
import { toast } from 'react-toastify';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { EditStudent } from '../Edit';
import { EditParentModal } from './EditParentModal';
import { ChangeParentPasswordModal, ParentPasswordTarget } from './ChangeParentPasswordModal';

interface StudentData {
    name: string;
    dob: string;
    gender: number;
    email: string;
    address: string;
    cmnd: string;
    bhytNum: string;
    studentCode: string;
    parentPhonenum?: string;
}

interface ParentData {
    parentId: number;
    name: string;
    phoneNumber: string;
    email?: string;
    dob?: string;
}

export const StudentInfo = ({ studentId: propStudentId }: { studentId?: string | number }) => {
    const { studentId: paramStudentId } = useParams();
    const studentId = propStudentId || paramStudentId;
    const [student, setStudent] = useState<StudentData | null>(null);
    const [parents, setParents] = useState<ParentData[]>([]);
    const [currentClass, setCurrentClass] = useState<string>('');
    const [showModal, setShowModal] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEditParentModalOpen, setIsEditParentModalOpen] = useState(false);
    const [selectedParentForEdit, setSelectedParentForEdit] = useState<ParentData | null>(null);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [selectedParentForPassword, setSelectedParentForPassword] = useState<ParentPasswordTarget | null>(null);
    const [creatingParent, setCreatingParent] = useState(false);
    const [form] = Form.useForm();
    const [currentClasses, setCurrentClasses] = useState<any[]>([]);
    const [pastClasses, setPastClasses] = useState<any[]>([]);

    // Check phụ huynh đã tồn tại theo SĐT
    const [isExistingParentModal, setIsExistingParentModal] = useState(false);
    const [existingParentIdModal, setExistingParentIdModal] = useState<number | null>(null);
    const [checkingPhoneModal, setCheckingPhoneModal] = useState(false);

    const handleCheckPhoneModal = async (phoneVal?: string) => {
        const raw = phoneVal !== undefined ? phoneVal : form.getFieldValue('phoneNumber');
        if (!raw || raw.trim().length < 9) {
            if (isExistingParentModal) {
                setIsExistingParentModal(false);
                setExistingParentIdModal(null);
            }
            return;
        }

        const clean = raw.trim();
        setCheckingPhoneModal(true);
        try {
            const res: ResponseDataType<any> = await getRequest(
                `/cms/student/get-parent-by-phoneNumber?phoneNumber=${encodeURIComponent(clean)}`
            );
            if (res?.code === 200 && res.info?.parentId) {
                setIsExistingParentModal(true);
                setExistingParentIdModal(res.info.parentId);
                form.setFieldsValue({
                    name: res.info.name || '',
                    email: res.info.email || '',
                    dob: parseFlexibleDate(res.info.dob),
                });
                toast.info(`Tìm thấy phụ huynh: ${res.info.name || clean}. Bấm thêm để liên kết.`);
            } else {
                if (isExistingParentModal) {
                    setIsExistingParentModal(false);
                    setExistingParentIdModal(null);
                }
            }
        } catch {
            setIsExistingParentModal(false);
            setExistingParentIdModal(null);
        } finally {
            setCheckingPhoneModal(false);
        }
    };

    // Fetch học sinh
    const fetchStudent = async () => {
        const res: ResponseDataType<StudentData> = await getRequest(`/cms/student/${studentId}`);
        if (res?.code === 200 && res.info) {
            setStudent(res.info);
        } else {
            toast.error('Không thể tải thông tin học sinh');
        }
    };

    // Fetch lớp hiện tại
    const fetchCurrentClass = async () => {
        const res: ResponseDataType<any> = await getRequest(
            `/cms/student/${studentId}/get-classYear-by-student?status=1`
        );
        if (res?.code === 200 && Array.isArray(res.info) && res.info.length > 0) {
            setCurrentClass(res.info[0].className || '');
        } else {
            setCurrentClass('');
        }
    };

    // Fetch phụ huynh của học sinh
    const fetchStudentParents = async () => {
        const response: ResponseDataType<ParentData[]> = await getRequest(
            `/cms/student/get-parent-by-student?studentId=${studentId}`
        );
        if (response?.code === 200 && Array.isArray(response.info)) {
            setParents(response.info);
        } else {
            setParents([]);
            toast.error('Không thể tải danh sách phụ huynh');
        }
    };
    const fetchClassesByStudent = async () => {
        // Lớp đang học
        const resCurrent = await getRequest(`/cms/student/${studentId}/get-classYear-by-student?status=1`);
        if (resCurrent?.code === 200 && Array.isArray(resCurrent.info)) {
            setCurrentClasses(resCurrent.info);
        } else {
            setCurrentClasses([]);
        }

        // Lớp đã học
        const resPast = await getRequest(`/cms/student/${studentId}/get-classYear-by-student?status=0`);
        if (resPast?.code === 200 && Array.isArray(resPast.info)) {
            setPastClasses(resPast.info);
        } else {
            setPastClasses([]);
        }
    };

    // Xử lý tạo và gắn phụ huynh mới
    const handleCreateAndAttachParent = async (values: any) => {
        setCreatingParent(true);
        try {
            let finalParentId = existingParentIdModal;
            const parentPhone = values.phoneNumber?.trim();

            if (!finalParentId && parentPhone) {
                // Kiểm tra lại 1 lần nữa trước khi tạo để tránh race condition
                const checkRes: ResponseDataType<any> = await getRequest(
                    `/cms/student/get-parent-by-phoneNumber?phoneNumber=${encodeURIComponent(parentPhone)}`
                );
                if (checkRes?.code === 200 && checkRes.info?.parentId) {
                    finalParentId = checkRes.info.parentId;
                } else {
                    const dobString = values.dob?.format?.("YYYY-MM-DD") || values.dob;
                    const response: ResponseDataType<any> = await postRequest(
                        '/cms/parent',
                        { ...values, dob: dobString, phoneNumber: parentPhone },
                        'json'
                    );

                    if (response?.code === 200 && response.info?.parentId) {
                        finalParentId = response.info.parentId;
                    } else if (response?.info?.parentId) {
                        finalParentId = response.info.parentId;
                    } else {
                        toast.error(response?.msg || 'Tạo phụ huynh thất bại!');
                        return;
                    }
                }
            }

            if (finalParentId) {
                const attachRes = await postRequest(`/cms/student/${studentId}/add-parent-to-student`, {
                    parentId: finalParentId,
                });

                if (attachRes?.code === 200) {
                    toast.success('Thêm phụ huynh thành công!');
                    setShowModal(false);
                    form.resetFields();
                    setIsExistingParentModal(false);
                    setExistingParentIdModal(null);
                    fetchStudentParents();
                    fetchStudent();
                } else {
                    toast.error('Tạo thành công nhưng gắn vào học sinh thất bại!');
                }
            } else {
                toast.error('Không tìm thấy thông tin phụ huynh để liên kết!');
            }
        } catch (error) {
            toast.error('Có lỗi xảy ra khi tạo phụ huynh');
        } finally {
            setCreatingParent(false);
        }
    };

    const handleDeleteStudentParent = (parentItem: ParentData) => {
        Modal.confirm({
            title: `Bạn có chắc chắn muốn hủy liên kết phụ huynh "${parentItem.name}" khỏi học sinh này?`,
            content: 'Hành động này sẽ gỡ phụ huynh ra khỏi danh sách của học sinh.',
            okText: 'Xác nhận xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            async onOk() {
                try {
                    const res: ResponseDataType<any> = await deleteRequest(
                        `/cms/student/${studentId}/delete-student-parent`,
                        { parentId: parentItem.parentId }
                    );
                    if (res?.code === 200) {
                        toast.success('Hủy liên kết phụ huynh thành công!');
                        fetchStudentParents();
                        fetchStudent();
                    } else {
                        toast.error(res?.msg || 'Hủy liên kết thất bại!');
                    }
                } catch (err) {
                    toast.error('Có lỗi xảy ra khi hủy liên kết phụ huynh');
                }
            },
        });
    };

    const openChangePasswordModal = (target: ParentPasswordTarget) => {
        setSelectedParentForPassword(target);
        setIsChangePasswordModalOpen(true);
    };

    const handleResetByStudentParentPhone = async (phone: string) => {
        try {
            const cleanPhone = phone.trim();
            const res: ResponseDataType<any> = await getRequest(
                `/cms/student/get-parent-by-phoneNumber?phoneNumber=${encodeURIComponent(cleanPhone)}`
            );
            if (res?.code === 200 && res.info?.parentId) {
                openChangePasswordModal({
                    parentId: res.info.parentId,
                    name: res.info.name || `Phụ huynh của học sinh`,
                    phoneNumber: cleanPhone,
                });
            } else {
                toast.warning(`Chưa tìm thấy tài khoản phụ huynh với số điện thoại ${cleanPhone}. Bạn có thể bấm "Thêm phụ huynh" để tạo tài khoản.`);
            }
        } catch {
            toast.error('Có lỗi xảy ra khi kiểm tra tài khoản phụ huynh');
        }
    };

    useEffect(() => {
        fetchStudent();
        fetchStudentParents();
        fetchCurrentClass();
        fetchClassesByStudent();
    }, [studentId]);

    return (
        <div>

            <Card
                title="Thông tin học sinh"
                style={{ marginBottom: 2 }}
                extra={
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => setIsEditModalOpen(true)}
                    >
                        Chỉnh sửa
                    </Button>
                }
            >
                <Table
                    dataSource={student ? [student] : []}
                    pagination={false}
                    columns={[
                        { title: 'Họ tên', dataIndex: 'name' },
                        { title: 'Ngày sinh', dataIndex: 'dob' },
                        { title: 'Giới tính', dataIndex: 'gender', render: val => (val === 1 ? 'Nam' : 'Nữ') },
                        { title: 'Email', dataIndex: 'email' },
                        { title: 'Lớp đang học', dataIndex: 'className', render: () => currentClass },
                        { title: 'Địa chỉ', dataIndex: 'address' },
                        { title: 'Số CMND/CCCD', dataIndex: 'cmnd' },
                        { title: 'Mã BHYT', dataIndex: 'bhytNum' },
                    ]}
                    rowKey={() => 'student'}
                />
            </Card>

            <Modal
                title="Chỉnh sửa thông tin học sinh"
                open={isEditModalOpen}
                onCancel={() => setIsEditModalOpen(false)}
                footer={null}
                width={700}
                destroyOnClose
            >
                {student && (
                    <EditStudent
                        studentData={{
                            studentId: Number(studentId),
                            ...student,
                        }}
                        onSuccess={() => {
                            fetchStudent();
                        }}
                        onClose={() => setIsEditModalOpen(false)}
                    />
                )}
            </Modal>
            <div style={{ backgroundColor: '#f5f8fa', padding: 16, borderRadius: 8, marginTop: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, marginBottom: 16 }}>
                    <h2 style={{ margin: 0 }}>Thông tin phụ huynh</h2>
                    <Space>
                        {parents.length === 0 && student?.parentPhonenum && (
                            <Button
                                style={{ color: '#fa8c16', borderColor: '#fa8c16' }}
                                onClick={() => handleResetByStudentParentPhone(student.parentPhonenum!)}
                            >
                                Đổi MK (theo SĐT: {student.parentPhonenum})
                            </Button>
                        )}
                        {parents.length === 0 && (
                            <Button type="primary" icon={<PlusOutlined />}
                                onClick={() => setShowModal(true)}>
                                Thêm phụ huynh
                            </Button>
                        )}
                    </Space>
                </div>
                <Table
                    dataSource={parents}
                    pagination={false}
                    columns={[
                        { title: 'STT', key: 'stt', align: 'center', render: (_, __, index) => index + 1 },
                        { title: 'Họ tên', dataIndex: 'name' },
                        { title: 'Email', dataIndex: 'email' },
                        { title: 'SĐT', dataIndex: 'phoneNumber' },
                        {
                            title: 'Hành động',
                            key: 'action',
                            align: 'center',
                            render: (_, record: ParentData) => (
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                                    <span
                                        style={{ color: '#52c41a', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                        onClick={() => {
                                            setSelectedParentForEdit(record);
                                            setIsEditParentModalOpen(true);
                                        }}
                                    >
                                        Sửa
                                    </span>
                                    <span
                                        style={{ color: '#fa8c16', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                        onClick={() => openChangePasswordModal(record)}
                                    >
                                        Đổi MK
                                    </span>
                                    <span
                                        style={{ color: 'red', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                        onClick={() => handleDeleteStudentParent(record)}
                                    >
                                        Xóa
                                    </span>
                                </div>
                            ),
                        },
                    ]}
                    rowKey={record => record?.parentId?.toString() ?? `parent-${Math.random()}`}
                />
            </div>

            <EditParentModal
                open={isEditParentModalOpen}
                parentData={selectedParentForEdit}
                onSuccess={() => {
                    fetchStudentParents();
                    fetchStudent();
                }}
                onClose={() => {
                    setIsEditParentModalOpen(false);
                    setSelectedParentForEdit(null);
                }}
            />

            <ChangeParentPasswordModal
                open={isChangePasswordModalOpen}
                parentData={selectedParentForPassword}
                onSuccess={() => {
                    fetchStudentParents();
                }}
                onClose={() => {
                    setIsChangePasswordModalOpen(false);
                    setSelectedParentForPassword(null);
                }}
            />

            <Card title="Lớp đang học" style={{ marginTop: 24 }}>
                <Table
                    dataSource={currentClasses}
                    pagination={false}
                    columns={[
                        { title: 'Tên lớp', dataIndex: 'className' },
                        { title: 'Khối', dataIndex: 'classLevel' },
                        { title: 'Năm học', dataIndex: 'schoolYear' },
                        { title: 'Năm bắt đầu', dataIndex: 'start' },
                        { title: 'Năm kết thúc', dataIndex: 'end' },
                    ]}
                    rowKey={record => record.classYearId}
                    locale={{ emptyText: 'Chưa có lớp đang học' }}
                />
            </Card>

            <Card title="Danh sách lớp đã học" style={{ marginTop: 24 }}>
                <Table
                    dataSource={pastClasses}
                    pagination={false}
                    columns={[
                        { title: 'Tên lớp', dataIndex: 'className' },
                        { title: 'Khối', dataIndex: 'classLevel' },
                        { title: 'Năm học', dataIndex: 'schoolYear' },
                        { title: 'Năm bắt đầu', dataIndex: 'start' },
                        { title: 'Năm kết thúc', dataIndex: 'end' },
                    ]}
                    rowKey={record => record.classYearId}
                    locale={{ emptyText: 'Không có lớp đã học' }}
                />
            </Card>




            <Modal
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 24 }}>
                        <span>Tạo và gắn phụ huynh</span>
                        {checkingPhoneModal ? (
                            <Spin size="small" />
                        ) : isExistingParentModal ? (
                            <Tag color="green">Đã có tài khoản</Tag>
                        ) : null}
                    </div>
                }
                open={showModal}
                onCancel={() => {
                    setShowModal(false);
                    form.resetFields();
                    setIsExistingParentModal(false);
                    setExistingParentIdModal(null);
                }}
                footer={null}
                destroyOnClose
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateAndAttachParent}
                    style={{ marginTop: 16 }}
                >
                    <Form.Item
                        label="Số điện thoại phụ huynh"
                        name="phoneNumber"
                        rules={[
                            { required: true, message: 'Vui lòng nhập số điện thoại' },
                            { pattern: /^[0-9]{9,11}$/, message: 'Số điện thoại không hợp lệ' }
                        ]}
                    >
                        <Input
                            placeholder="Nhập số điện thoại"
                            onBlur={() => handleCheckPhoneModal()}
                            onChange={(e) => {
                                const val = e.target.value?.trim();
                                if (val && val.length >= 9) {
                                    handleCheckPhoneModal(val);
                                } else if (isExistingParentModal) {
                                    setIsExistingParentModal(false);
                                    setExistingParentIdModal(null);
                                }
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Tên phụ huynh"
                        name="name"
                        rules={[{ required: true, message: 'Vui lòng nhập tên phụ huynh' }]}
                    >
                        <Input placeholder="Nhập tên phụ huynh" disabled={isExistingParentModal} />
                    </Form.Item>

                    <Form.Item
                        label="Email"
                        name="email"
                        rules={[
                            { type: 'email', message: 'Email không hợp lệ' },
                        ]}
                    >
                        <Input placeholder="Nhập email" disabled={isExistingParentModal} />
                    </Form.Item>

                    <Form.Item label="Ngày sinh" name="dob">
                        <DatePicker
                            style={{ width: '100%' }}
                            format="YYYY-MM-DD"
                            placeholder="Chọn ngày sinh"
                            disabled={isExistingParentModal}
                        />
                    </Form.Item>

                    {isExistingParentModal && (
                        <div style={{ marginBottom: 16, fontSize: '13px', color: '#52c41a', background: '#f6ffed', border: '1px solid #b7eb8f', padding: '8px 12px', borderRadius: 4 }}>
                            ✓ Phụ huynh này đã có tài khoản trên hệ thống. Bấm nút dưới để liên kết với học sinh.
                        </div>
                    )}

                    <Form.Item style={{ marginBottom: 0, textAlign: 'right', marginTop: 16 }}>
                        <Space>
                            <Button onClick={() => setShowModal(false)} disabled={creatingParent}>
                                Hủy
                            </Button>
                            <Button type="primary" htmlType="submit" loading={creatingParent}>
                                {isExistingParentModal ? 'Liên kết phụ huynh' : 'Thêm phụ huynh'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
